import React, { useEffect, useState } from 'react';
import { vaultsService, vaultFoldersService, vaultPasswordsService, basicService } from '@app/services';
import {
    defaultNewVaultPassword,
    passwordTypeDefault,
    passwordTypes,
    getPasswordTypeUIDetails,
    dbTypes,
    authenticationProfiles,
    encryptionProtocols,
    defaultOrgId,
    localStore,
    passwordComplexities,
    defaultComplexity,
    defaultAuthenticationEncryption,
    dbTypeDefault,
    active,
    replaceDynamicTranslation,
    trimStringsOnObject,
    Network_Device,
    Remote_Windows,
    Linux,
    SSH,
    Standalone_Windows,
    AD_Windows,
    Database,
    Web,
    SNMP,
    SNMPV3,
    AuthAnvil_Related,
    General,
    WebsiteUrl,
    PasswordType,
    MachineName,
    Domain,
    SnmpAgentPort,
    SnmpCommunityString,
    DaysToExpire,
    Username,
    Password,
    SnmpUser,
    SnmpAuthenticationPassword,
    SnmpPrivacyPassword,
    DBType,
    AgentProfileId,
    getShareablePasswordLink,
    getActiveVaultPermission,
    getMenuItemById
} from '@app/helpers';
import {
    VaultPasswordAddEdit,
    VaultPasswordDisplayFields,
    IVaultFolder,
    Order,
    IVaultPasswordPolicyRequirements,
    PwSSyncAgentInstallTypeEnum,
    IVaultPasswordUrl,
    ISyncAgentsDisplay
} from '@app/models';
import { Grid, Typography, Box, IconButton } from '@material-ui/core';
import { PasslyOutlinedBox } from '@app/componentsV2';
import { useSelector, useDispatch } from 'react-redux';
import { useSetPage, useTypedTranslation, useUrlParams } from '@app/hooks';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { useParams } from 'react-router';
import { FormMode } from '@app/helpers/enums';
import VaultPasswordComponent from '../components/vaultPassword.component';
import _ from 'lodash';
import { HelpOutline, Link } from '@material-ui/icons';
import { PageLoader, PasslyButton, CopyComponent, PasslyCheckbox, PasslySelect, IMenuItem, mainMenuTypes, PageActions, PasslyTextField } from '@app/components';
import { vaultAdd, vaultUpdate } from '@app/store/constants';
import { customBreadcrumbs, setExceptionHandler, getLoaderBtn } from '@app/store/actions';
import { confirm } from '@app/components/Modal/confirmModal';
import Popover from '@material-ui/core/Popover';
import PasswordPolicyRequirements from '@app/pages/password/dialogs/passwordPolicyRequirements.dialog';
import { InputNumber } from '@itglue/kaseya-ui';

const AddEditVaultPasswordPage: React.SFC = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.VaultManager);
    const { id: vaultId, passwordId } = useParams<{ id: string; passwordId: string }>();
    const { vaultName } = useUrlParams<{ vaultName: string }>();
    const { isIndividual } = useUrlParams<{ isIndividual: string }>();
    const { folderId } = useUrlParams<{ folderId: string }>();
    const maxExpireAfterPreviewHours: number = 165;

    const viewType = passwordId === undefined || passwordId === '' ? FormMode.Add : FormMode.Edit;
    const userVaultsPermissions = useSelector((state: any) => state.signIn.currentUser.vaultPermissions);
    const permissions = userVaultsPermissions ? getActiveVaultPermission(userVaultsPermissions[vaultId]) : new Set<string>();

    const hasPermission: boolean =
        (viewType === FormMode.Add && permissions?.has(vaultAdd)) || (viewType === FormMode.Edit && permissions?.has(vaultUpdate));
    const noSyncAgentDefault: ISyncAgentsDisplay = {
        label: translation('NONE'),
        value: 'none',
        state: active,
        agentType: PwSSyncAgentInstallTypeEnum.thirdParty
    };

    const [addEditVaultPassword, setAddEditVaultPassword] = useState<VaultPasswordAddEdit>(defaultNewVaultPassword);
    const [passwordType, setPasswordType] = useState<string>(passwordTypeDefault);
    const [folders, setFolders] = useState<IVaultFolder[]>([]);
    const [allSyncAgents, setAllSyncAgents] = useState<ISyncAgentsDisplay[]>([noSyncAgentDefault]);
    const [availableSyncAgents, setAvailableSyncAgents] = useState<ISyncAgentsDisplay[]>([noSyncAgentDefault]);
    const [hasThirdPartyAgent, setHasThirdPartyAgent] = useState<boolean>(false);
    const [isAgentDisabled, setIsAgentDisabled] = useState<boolean>(false);
    const [displayFields, setDisplayFields] = useState<VaultPasswordDisplayFields>({} as VaultPasswordDisplayFields);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [daysToExpireDefault, setDaysToExpireDefault] = useState(42);
    const [originalPassword, setOriginalPassword] = useState('');
    const [shareableLink, setShareableLink] = useState<string>('');

    // Errors
    const [displayNameError, setDisplayNameError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [domainError, setDomainError] = useState('');
    const [snmpUserError, setSnmpUserError] = useState('');
    const [webUrlError, setWebUrlError] = useState('');
    const [machineNameError, setMachineNameError] = useState('');
    const [snmpAgentPortError, setSnmpAgentPortError] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [communityStringError, setCommunityStringError] = useState('');
    const [authenticationPasswordError, setAuthenticationPasswordError] = useState('');
    const [privacyPasswordError, setPrivacyPasswordError] = useState('');
    const [expireOnPreviewHoursError, setExpireOnPreviewHoursError] = useState(false);
    const [expireOnPreviewMinutesError, setExpireOnPreviewMinutesError] = useState(false);
    const [daysToExpireError, setDaysToExpireError] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [canExpireOnView, setCanExpireOnView] = useState(false);
    const [dontSyncPassword, setDontSyncPassword] = useState(false);
    const [expireOnPreviewMinutes, setExpireOnPreviewMinutes] = useState<number>(0);
    const [expireOnPreviewHours, setExpireOnPreviewHours] = useState<number>(0);
    const [passwordPolicies, setPasswordPolicies] = useState<IVaultPasswordPolicyRequirements[]>([]);
    const [openPasswordPolicyRequirements, setOpenPasswordPolicyRequirements] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [openPasswordSharingHelper, setOpenPasswordSharingHelper] = useState<boolean>(false);
    const [vaultAllPasswords, setVaultAllPasswords] = useState<IVaultPasswordUrl[]>([]);

    const availableOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);

    useSetPage(null, item);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (hasPermission) {
            if (viewType === FormMode.Edit) {
                vaultPasswordsService.getVaultPasswordByPasswordId(vaultId, passwordId, handleSaveError, handleGetPasswordSuccess);
            }

            vaultFoldersService.getAllVaultFolders(vaultId, Order.asc).then(handleFolderSuccess);

            // nothing to do if fails
            vaultsService.getAllVaultSyncAgents(vaultId).then(handleGetAgentsSuccess);

            vaultPasswordsService.getPasswordsInfo(100).then((values: IVaultPasswordUrl[]) => setVaultAllPasswords(values));
        }

        handleChangeForm(passwordTypeDefault);
    }, []);

    useEffect(() => {
        if (vaultName !== undefined && vaultName !== '') {
            dispatch(
                customBreadcrumbs([
                    {
                        name: translation('PASSWORD_SERVER')
                    },
                    {
                        name: translation('APP_VAULT_MANAGER'),
                        link: '/passwordserver/vaults'
                    },
                    {
                        name: vaultName,
                        link: `/passwordserver/vaults/${vaultId}`
                    },
                    {
                        name: viewType === FormMode.Add ? translation('NEW_PASSWORD') : translation('EDIT_PASSWORD_SERVER_VAULT_PASSWORD')
                    }
                ])
            );
        }
    }, [vaultName]);

    useEffect(() => {
        if (viewType === FormMode.Add) {
            if (folderId && folderId !== '') {
                let addEditPasswordCopy = { ...addEditVaultPassword };
                addEditPasswordCopy.FolderId = folderId;

                setAddEditVaultPassword(addEditPasswordCopy);
            }
        }
    }, [folderId]);

    useEffect(() => {
        if (viewType === FormMode.Edit && addEditVaultPassword.FolderId !== '' && folders.length > 0) {
            setShareableLink(getShareablePasswordLink(passwordId));
        }
    }, [addEditVaultPassword.FolderId, folders]);

    const handleChangeForm = (value: string) => {
        let copyAddEditVaultPassword = { ...addEditVaultPassword };
        let display = { ...displayFields };

        copyAddEditVaultPassword.PasswordType = value;

        if (copyAddEditVaultPassword.PasswordType !== AD_Windows && copyAddEditVaultPassword.PasswordType !== Standalone_Windows) {
            if (hasThirdPartyAgent) {
                showThirdPartyAgentsOnly();
                display.ShowSyncAgent = true;

                handleValidAgentTypeForSelectedPassword(copyAddEditVaultPassword, [PwSSyncAgentInstallTypeEnum.thirdParty]);
            } else {
                if (copyAddEditVaultPassword.AgentProfileId !== null && copyAddEditVaultPassword.AgentProfileId !== '') {
                    copyAddEditVaultPassword.AgentProfileId = 'none';
                }

                setIsAgentDisabled(false);
                display.ShowSyncAgent = false;
            }
        }

        if (copyAddEditVaultPassword.PasswordType === AD_Windows) {
            handleValidAgentTypeForSelectedPassword(copyAddEditVaultPassword, [
                PwSSyncAgentInstallTypeEnum.activeDirectoryDomain,
                PwSSyncAgentInstallTypeEnum.thirdParty
            ]);
        }

        switch (copyAddEditVaultPassword.PasswordType) {
            case AD_Windows:
                display.ShowDomain = true;
                display.ShowMachineName = false;
                display.ShowWebsiteUrl = false;
                display.ShowSyncAgent = true;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                setChosenAgentProvisioningStatus(copyAddEditVaultPassword.AgentProfileId || '');
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.MachineName = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
            case Standalone_Windows:
                display.ShowDomain = false;
                display.ShowMachineName = true;
                display.ShowWebsiteUrl = false;
                display.ShowSyncAgent = true;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                setChosenAgentProvisioningStatus(copyAddEditVaultPassword.AgentProfileId || '');
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
            case Network_Device:
            case Remote_Windows:
            case Linux:
            case SSH:
                display.ShowDomain = false;
                display.ShowMachineName = true;
                display.ShowWebsiteUrl = false;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
            case Database:
                display.ShowDomain = false;
                display.ShowMachineName = true;
                display.ShowWebsiteUrl = false;
                display.ShowDBTypes = true;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.DBType = dbTypeDefault;
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
            case Web:
                display.ShowDomain = false;
                display.ShowMachineName = false;
                display.ShowWebsiteUrl = true;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.MachineName = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
            case SNMP:
                display.ShowDomain = false;
                display.ShowMachineName = true;
                display.ShowWebsiteUrl = false;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = true;
                display.ShowSnmpAgentPort = true;
                display.ShowSnmpAuthenticationProfile = true;
                display.ShowSnmpEncryptionProtocol = true;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = false;
                display.ShowPassword = false;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.Username = '';
                copyAddEditVaultPassword.Password = '';
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = defaultAuthenticationEncryption;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = defaultAuthenticationEncryption;
                break;
            case SNMPV3:
                display.ShowDomain = false;
                display.ShowMachineName = true;
                display.ShowWebsiteUrl = false;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = true;
                display.ShowSnmpAuthenticationProfile = true;
                display.ShowSnmpEncryptionProtocol = true;
                display.ShowSnmpUser = true;
                display.ShowSnmpAuthenticationPassword = true;
                display.ShowSnmpPrivacyPassword = true;
                display.ShowUsername = false;
                display.ShowPassword = false;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.Username = '';
                copyAddEditVaultPassword.Password = '';
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = defaultAuthenticationEncryption;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = defaultAuthenticationEncryption;
                break;
            case AuthAnvil_Related:
            case General:
            default:
                display.ShowDomain = false;
                display.ShowMachineName = false;
                display.ShowWebsiteUrl = false;
                display.ShowDBTypes = false;
                display.ShowDBOther = false;
                display.ShowSnmpCommunityString = false;
                display.ShowSnmpAgentPort = false;
                display.ShowSnmpAuthenticationProfile = false;
                display.ShowSnmpEncryptionProtocol = false;
                display.ShowSnmpUser = false;
                display.ShowSnmpAuthenticationPassword = false;
                display.ShowSnmpPrivacyPassword = false;
                display.ShowUsername = true;
                display.ShowPassword = true;
                display.ShowDisabledAgent = false;
                copyAddEditVaultPassword.Domain = '';
                copyAddEditVaultPassword.MachineName = '';
                copyAddEditVaultPassword.WebsiteUrl = '';
                copyAddEditVaultPassword.DBType = '';
                copyAddEditVaultPassword.DBTypeOther = '';
                copyAddEditVaultPassword.SnmpCommunityString = '';
                copyAddEditVaultPassword.SnmpAgentPort = 0;
                copyAddEditVaultPassword.SnmpAuthenticationProfile = '';
                copyAddEditVaultPassword.SnmpEncryptionProtocol = '';
                copyAddEditVaultPassword.SnmpUser = '';
                copyAddEditVaultPassword.SnmpAuthenticationPassword = '';
                copyAddEditVaultPassword.SnmpPrivacyPassword = '';
                break;
        }

        setAddEditVaultPassword(copyAddEditVaultPassword);
        setDisplayFields(display);
    };

    useEffect(() => {
        if (addEditVaultPassword.DBType === 'Other') {
            let displayFieldsCopy = { ...displayFields };

            displayFieldsCopy.ShowDBOther = true;
            setDisplayFields(displayFieldsCopy);
        }
    }, [addEditVaultPassword]);

    useEffect(() => {
        const minutesValue = typeof expireOnPreviewMinutes === 'number' ? (expireOnPreviewMinutes as number) : -1;
        const hoursValue = typeof expireOnPreviewHours === 'number' ? (expireOnPreviewHours as number) : -1;
        let finalMinutes = -1;
        if (hoursValue !== -1 && minutesValue !== -1) {
            finalMinutes = hoursValue * 60 + minutesValue;
        }

        if (hoursValue < 0 || hoursValue > maxExpireAfterPreviewHours) {
            setExpireOnPreviewHoursError(true);
        } else {
            setExpireOnPreviewHoursError(false);
        }

        if (minutesValue < 0 || minutesValue > 59) {
            setExpireOnPreviewMinutesError(true);
        } else {
            setExpireOnPreviewMinutesError(false);
        }

        let addEditPasswordCopy = { ...addEditVaultPassword };
        addEditPasswordCopy.ExpireAfterXMinutes = finalMinutes;
        setAddEditVaultPassword(addEditPasswordCopy);
    }, [expireOnPreviewHours, expireOnPreviewMinutes]);

    useEffect(() => {
        if (loaded && addEditVaultPassword.DisplayName === '') {
            setDisplayNameError(translation('REQUIRED'));
        } else {
            setDisplayNameError('');
        }
    }, [addEditVaultPassword.DisplayName]);

    useEffect(() => {
        // only load the policy defaults after folders so
        // the password object isn't overwritten
        if (addEditVaultPassword.FolderId !== '' && !loaded) {
            vaultsService.getVaultPasswordPolicyDefaults(vaultId, handlePasswordPolicyDefaults);
        }
    }, [addEditVaultPassword.FolderId]);

    const handlePasswordPolicyDefaults = (response: any) => {
        if (!addEditVaultPassword.IgnoreVaultPasswordPolicy) {
            let addEditPasswordCopy = { ...addEditVaultPassword };
            addEditPasswordCopy.DaysToExpire = response.data.DaysToExpiration;
            setAddEditVaultPassword(addEditPasswordCopy);
        }

        let defaultPolicy: IVaultPasswordPolicyRequirements[] = [
            {
                value: defaultComplexity,
                label: response.data.DisplayName + ` (${translation('DEFAULT')})`,
                MaximumLength: response.data.MaximumLength,
                MinimumLength: response.data.MinimumLength,
                RequireDigits: response.data.RequireDigits,
                RequireLowercase: response.data.RequireLowercase,
                RequireUppercase: response.data.RequireUppercase,
                RequireSpecials: response.data.RequireSpecials,
                SpecialCharactersUsed: '',
                OnlyNumbers: false
            }
        ];

        setPasswordPolicies([...defaultPolicy, ...passwordComplexities]);
        setLoaded(true);
    };

    const handleFolderSuccess = (values: any) => {
        setFolders(values);
    };

    const resolveOrgName = (orgId: string) => {
        if (orgId) {
            let org = _.find(availableOrgs, { id: orgId });
            if (org) {
                return org.name;
            }
        }
        return null;
    };

    const showThirdPartyAgentsOnly = () => {
        let thirdPartyAgents: ISyncAgentsDisplay[] = [];
        for (let i = 0; i < allSyncAgents.length; i++) {
            if (allSyncAgents[i].agentType === PwSSyncAgentInstallTypeEnum.thirdParty) {
                thirdPartyAgents.push(allSyncAgents[i]);
            }
        }

        setAvailableSyncAgents(thirdPartyAgents);
    };

    useEffect(() => {
        if (addEditVaultPassword.PasswordType === AD_Windows || addEditVaultPassword.PasswordType === Standalone_Windows) {
            setAvailableSyncAgents(allSyncAgents);
        }
    }, [addEditVaultPassword.PasswordType]);

    const handleGetAgentsSuccess = (value: any) => {
        let agents = value;

        if (agents.length > 0) {
            let availableAgentsCopy = [...availableSyncAgents];
            let allAgentsCopy = [...allSyncAgents];
            const localStorageOrganization = localStore.getItem('org_filter') ? localStore.getItem('org_filter') : '';

            agents.map((agent: any) => {
                let display = agent.AgentName;

                // Get the org name for display with the agent name
                if (localStorageOrganization === defaultOrgId) {
                    const orgName = resolveOrgName(agents.OrganizationId);

                    if (orgName) {
                        display += ' (' + orgName + ')';
                    }
                }

                if (agent.AgentType === PwSSyncAgentInstallTypeEnum.thirdParty) {
                    setHasThirdPartyAgent(true);
                }

                availableAgentsCopy.push({ label: display, value: agent.Id, state: agent.ProvisioningState, agentType: agent.AgentType });
                allAgentsCopy.push({ label: display, value: agent.Id, state: agent.ProvisioningState, agentType: agent.AgentType });
            });

            // On first load the page needs to be updated
            // to show the sync agent dropdown
            if (hasThirdPartyAgent) {
                let displayFieldsCopy = { ...displayFields };
                displayFieldsCopy.ShowSyncAgent = true;
                setDisplayFields(displayFieldsCopy);
            }

            setAllSyncAgents(allAgentsCopy);
            setAvailableSyncAgents(availableAgentsCopy);
        }
    };

    const handleValidAgentTypeForSelectedPassword = (pwsAgentProfile: VaultPasswordAddEdit, validAgentTypes: PwSSyncAgentInstallTypeEnum[]) => {
        if (pwsAgentProfile.AgentProfileId === null || pwsAgentProfile.AgentProfileId === '') {
            return;
        }

        let agent = _.find(availableSyncAgents, { value: pwsAgentProfile.AgentProfileId });
        let agentIsValidType: boolean = false;

        if (agent && validAgentTypes.includes(agent.agentType)) {
            agentIsValidType = true;
        }

        if (!agentIsValidType) {
            pwsAgentProfile.AgentProfileId = 'none';
            setIsAgentDisabled(false);
        }
    };

    const setChosenAgentProvisioningStatus = (agentId: string) => {
        let agent = _.find(availableSyncAgents, { value: agentId });
        if (agent && agent.state !== active) {
            setIsAgentDisabled(true);
            dispatch(handleSetSnackbar({ show: true, message: translation('INACTIVE_AGENT_PASSWORD_BLOCK'), severity: 'info', dismissOnly: true }));
        } else {
            setIsAgentDisabled(false);
        }
    };

    const validateDataChange = (e: any) => {
        const { name, value } = e.target;
        const errorText = value === undefined || value === '' ? translation('REQUIRED') : '';
        if (name === 'DisplayName') {
            setDisplayNameError(errorText);
        }

        if (name === DaysToExpire) {
            const intValue = parseInt(value);
            if (intValue < 0 || intValue > 9999 || isNaN(intValue)) {
                setDaysToExpireError(true);
            } else {
                setDaysToExpireError(false);
            }
        } else if (name === Username) {
            if (errorText === '' && !value.match(/^[A-Za-z0-9\.!@#_-]{0,256}$/)) {
                setUsernameError(translation('INVALID_FIELD_CHARACTERS'));
            } else {
                setUsernameError(errorText);
            }
        } else if (name === Password) {
            setPasswordError(errorText);
        } else if (name === SnmpUser) {
            setSnmpUserError(errorText);
        } else if (name === SnmpAuthenticationPassword) {
            setAuthenticationPasswordError(errorText);
        } else if (name === SnmpPrivacyPassword) {
            setPrivacyPasswordError(errorText);
        } else if (name === SnmpCommunityString) {
            setCommunityStringError(errorText);
        } else if (name === SnmpAgentPort) {
            const intValue = parseInt(value);
            if (intValue < 0 || intValue > 65535 || isNaN(intValue)) {
                setSnmpAgentPortError(true);
            } else {
                setSnmpAgentPortError(false);
            }
        } else if (name === Domain) {
            setDomainError(errorText);
        } else if (name === MachineName && (passwordType === Remote_Windows || passwordType === Database)) {
            setMachineNameError(errorText);
        } else if (name === WebsiteUrl) {
            setWebUrlError(errorText);
        }
    };

    const handleDataChange = (e: any) => {
        const { name, value } = e.target;

        if (name === PasswordType) {
            handleChangeForm(value);
            setPasswordType(value);
            return;
        }

        let newData = { ...addEditVaultPassword };

        if (name === DBType && value === 'Other') {
            let displayFieldsCopy = { ...displayFields };
            displayFieldsCopy.ShowDBOther = true;
            setDisplayFields(displayFieldsCopy);
        } else if (name === DBType && value !== 'Other') {
            let displayFieldsCopy = { ...displayFields };
            displayFieldsCopy.ShowDBOther = false;
            setDisplayFields(displayFieldsCopy);

            newData.DBTypeOther = '';
        }

        if (name === AgentProfileId) {
            setChosenAgentProvisioningStatus(value);
        }

        newData[name] = value;
        setAddEditVaultPassword(newData);
        validateDataChange(e);
    };

    const handleCheckboxDataChange = (checked: boolean, e: any) => {
        const { name } = e.target;

        let newData = { ...addEditVaultPassword };

        newData[name] = checked;
        setAddEditVaultPassword(newData);
    };

    const isValidSubmit = (): boolean => {
        let isValid = true;

        if (addEditVaultPassword.DisplayName === '') {
            isValid = false;
        }

        if (
            passwordType !== SNMP &&
            passwordType !== SNMPV3 &&
            (addEditVaultPassword.Username === '' || addEditVaultPassword.Password === '' || usernameError !== '')
        ) {
            isValid = false;
        }

        if (expireOnPreviewHoursError || expireOnPreviewMinutesError || daysToExpireError) {
            isValid = false;
        }

        if (canExpireOnView && addEditVaultPassword.ExpireAfterXMinutes < 0) {
            isValid = false;
        }

        switch (passwordType) {
            case AD_Windows:
                isValid = addEditVaultPassword.Domain === '' ? false : isValid;
                break;
            case Remote_Windows:
            case Database:
                isValid = addEditVaultPassword.MachineName === '' ? false : isValid;
                break;
            case Web:
                isValid = addEditVaultPassword.WebsiteUrl === '' ? false : isValid;
                break;
            case SNMP:
                isValid = addEditVaultPassword.SnmpCommunityString === '' || snmpAgentPortError ? false : isValid;
                break;
            case SNMPV3:
                isValid =
                    snmpAgentPortError ||
                    addEditVaultPassword.SnmpUser === '' ||
                    addEditVaultPassword.SnmpPrivacyPassword === '' ||
                    addEditVaultPassword.SnmpAuthenticationPassword === ''
                        ? false
                        : isValid;
                break;
            default:
                break;
        }

        return isValid;
    };
    const findTheSameUsernameIndex = (newUsername: string, newWebsiteUrl: string) => (password: IVaultPasswordUrl) =>
        password.Username === newUsername && password.WebsiteUrl === newWebsiteUrl && password.VaultPasswordId !== passwordId;
    const checkIfHaveTheSameUsername = (passwordState: VaultPasswordAddEdit, passwords: IVaultPasswordUrl[]) => {
        let haveTheSameUsername = false;

        if (passwords?.length) {
            const newUsername = passwordState?.Username;
            const newWebsiteUrl = passwordState?.WebsiteUrl;

            haveTheSameUsername = passwords.findIndex(findTheSameUsernameIndex(newUsername, newWebsiteUrl)) !== -1;
        }

        return haveTheSameUsername;
    };

    const handleSaveData = (continueWithWarning: boolean) => {
        if (!isValidSubmit()) {
            Object.keys(addEditVaultPassword).forEach(function (key) {
                validateDataChange({ target: { name: key, value: addEditVaultPassword[key] } });
            });
            return;
        }

        let passwordForSubmission = { ...addEditVaultPassword };
        passwordForSubmission.VaultId = vaultId;

        if (addEditVaultPassword.Complexity === defaultComplexity) {
            passwordForSubmission.Complexity = '';
        }

        if (addEditVaultPassword.AgentProfileId === 'none') {
            passwordForSubmission.AgentProfileId = null;
        }

        if (passwordType !== Database) {
            passwordForSubmission.DBType = '';
            passwordForSubmission.DBTypeOther = '';
        }

        if (passwordType === SNMPV3 || passwordType === SNMP) {
            // password can't be null
            passwordForSubmission.Password = '';
        } else {
            passwordForSubmission.SnmpCommunityString = '';
            passwordForSubmission.SnmpAuthenticationPassword = '';
            passwordForSubmission.SnmpPrivacyPassword = '';
            passwordForSubmission.SnmpAuthenticationProfile = '';
            passwordForSubmission.SnmpEncryptionProtocol = '';
            passwordForSubmission.SnmpUser = '';
        }

        // don't trim passwords
        trimStringsOnObject(passwordForSubmission, [Password, SnmpAuthenticationPassword, SnmpPrivacyPassword, SnmpCommunityString]);

        let json: any = {
            VaultPassword: passwordForSubmission,
            ContinueWithWarning: continueWithWarning
        };

        dispatch(getLoaderBtn(true));
        if (viewType === FormMode.Add) {
            vaultPasswordsService.addVaultPassword(json, vaultId, handleAddSuccess, handleSaveError);
        } else {
            json.VaultPasswordId = passwordId;
            json.PasswordChanged = passwordForSubmission.Password !== originalPassword;
            json.DoNotSyncPassword = dontSyncPassword;

            vaultPasswordsService.updateVaultPassword(json, vaultId, handleUpdateSuccess, handleSaveError);
        }
    };

    const handleSubmit = () => {
        const alreadyHaveTheSameUsername = checkIfHaveTheSameUsername(addEditVaultPassword, vaultAllPasswords);

        if (alreadyHaveTheSameUsername) {
            confirm(
                translation('PASSWORD_RECORD_ALREADY_EXISTED_2'),
                translation('CONTINUE'),
                translation('CANCEL'),
                translation('PASSWORD_RECORD_ALREADY_EXISTED_1')
            ).then(() => {
                handleSaveData(false);
            });
        } else {
            handleSaveData(false);
        }
    };

    const handleGetPasswordSuccess = (response: any) => {
        const passwordToSet: VaultPasswordAddEdit = {
            '@odata.type': 'Kaseya.AuthAnvil.Models.VaultPassword',
            FolderId: response.data.FolderId,
            VaultId: response.data.VaultId,
            Password: response.data.Password,
            DisplayName: response.data.DisplayName,
            Description: response.data.Description,
            IconUrl: response.data.IconUrl,
            PasswordType: response.data.PasswordType,
            Complexity: response.data.Complexity,
            MachineName: response.data.MachineName,
            Domain: response.data.Domain,
            DaysToExpire: response.data.DaysToExpire,
            ExpireAfterXMinutes: response.data.ExpireAfterXMinutes,
            EnableTrustedThirdPartyAccess: response.data.EnableTrustedThirdPartyAccess,
            Username: response.data.Username,
            WebsiteUrl: response.data.WebsiteUrl,
            IgnoreVaultPasswordPolicy: response.data.IgnoreVaultPasswordPolicy,
            Notes: response.data.Notes,
            AgentProfileId: response.data.AgentProfileId ? response.data.AgentProfileId : noSyncAgentDefault.value,
            DBType: response.data.DBType,
            DBTypeOther: response.data.DBTypeOther,
            SnmpAgentPort: response.data.SnmpAgentPort,
            SnmpAuthenticationPassword: response.data.SnmpAuthenticationPassword,
            SnmpAuthenticationProfile: response.data.SnmpAuthenticationProfile,
            SnmpCommunityString: response.data.SnmpCommunityString,
            SnmpEncryptionProtocol: response.data.SnmpEncryptionProtocol,
            SnmpPrivacyPassword: response.data.SnmpPrivacyPassword,
            SnmpUser: response.data.SnmpUser
        };

        if (!passwordToSet.IgnoreVaultPasswordPolicy && passwordToSet.Complexity === '') {
            passwordToSet.Complexity = defaultComplexity;
        }

        handleChangeForm(passwordToSet.PasswordType);
        setPasswordType(passwordToSet.PasswordType);
        setAddEditVaultPassword(passwordToSet);
        setOriginalPassword(response.data.Password);
        setExpireOnPreviewHours(Math.floor(passwordToSet.ExpireAfterXMinutes / 60));
        setExpireOnPreviewMinutes(passwordToSet.ExpireAfterXMinutes % 60);
        setCanExpireOnView(passwordToSet.ExpireAfterXMinutes > 0);
    };

    const duplicateSyncedPasswordDialog = () => {
        confirm(
            translation('PWS_SYNCAGENT_ALREADY_USED_WARNING'),
            translation('SAVE'),
            translation('CANCEL'),
            translation('PWS_SYNCAGENT_ALREADY_USED')
        ).then(() => {
            handleSaveData(true);
        });
    };

    const handleCancel = () => {
        const url: string = getReturnUrl();
        basicService.redirect(url);
    };

    const finishSuccess = (message: string) => {
        dispatch(getLoaderBtn(false));
        dispatch(handleSetSnackbar({ show: true, message: message, severity: 'success', dismissOnly: false }));

        const url: string = getReturnUrl();
        basicService.redirect(url);
    };

    const getReturnUrl = () => {
        let addEditPasswordCopy = { ...addEditVaultPassword };
        const id: string = addEditPasswordCopy.FolderId;

        const folder: IVaultFolder[] = folders.filter((folder: IVaultFolder) => folder.Id === id);
        const folderName: string = folder[0].DisplayName;

        let encodedFolderName = encodeURIComponent(folderName);
        const url = `/passwordserver/vaults/${vaultId}?folderId=${id}&folderName=${encodedFolderName}`;

        return url;
    };

    const handleAddSuccess = () => {
        finishSuccess(replaceDynamicTranslation(translation('GENERIC_ADD_SUCCESS_SINGLE'), translation('PASSWORD')));
    };

    const handleUpdateSuccess = () => {
        finishSuccess(replaceDynamicTranslation(translation('GENERIC_UPDATE_SINGLE'), translation('PASSWORD')));
    };

    const handleSaveError = (error: any) => {
        dispatch(getLoaderBtn(false));
        const errorMsg = error.response.data.error.message;
        if (errorMsg === translation('PWS_SYNCAGENT_ALREADY_USED')) {
            duplicateSyncedPasswordDialog();
            return;
        }
        setSaveError(errorMsg);
    };

    const handleUseVaultPasswordPolicy = (checked: boolean) => {
        let addEditPasswordCopy = { ...addEditVaultPassword };

        addEditPasswordCopy.IgnoreVaultPasswordPolicy = checked;
        addEditPasswordCopy.Complexity = checked ? '' : defaultComplexity;
        addEditPasswordCopy.DaysToExpire = daysToExpireDefault;
        validateDataChange({ target: { name: 'DaysToExpire', value: daysToExpireDefault } });
        setAddEditVaultPassword(addEditPasswordCopy);
    };

    const generatePasswordGeneric = (generatedPwd: string) => {
        let addEditPasswordCopy = { ...addEditVaultPassword };
        addEditPasswordCopy.Password = generatedPwd;
        setAddEditVaultPassword(addEditPasswordCopy);

        const event = { target: { value: generatedPwd, name: Password } };
        validateDataChange(event);
    };

    const generateCommunityString = (generatedPwd: string) => {
        let addEditPasswordCopy = { ...addEditVaultPassword };
        addEditPasswordCopy.SnmpCommunityString = generatedPwd;
        setAddEditVaultPassword(addEditPasswordCopy);

        const event = { target: { value: generatedPwd, name: SnmpCommunityString } };
        validateDataChange(event);
    };

    const generatePasswordAuth = (generatedPwd: string) => {
        let addEditPasswordCopy = { ...addEditVaultPassword };
        addEditPasswordCopy.SnmpAuthenticationPassword = generatedPwd;
        setAddEditVaultPassword(addEditPasswordCopy);

        const event = { target: { value: generatedPwd, name: SnmpAuthenticationPassword } };
        validateDataChange(event);
    };

    const generatePasswordPrivacy = (generatedPwd: string) => {
        let addEditPasswordCopy = { ...addEditVaultPassword };
        addEditPasswordCopy.SnmpPrivacyPassword = generatedPwd;
        setAddEditVaultPassword(addEditPasswordCopy);

        const event = { target: { value: generatedPwd, name: SnmpPrivacyPassword } };
        validateDataChange(event);
    };

    const handleCanExpireOnView = (isChecked: boolean) => {
        setExpireOnPreviewHours(0);
        isChecked ? setExpireOnPreviewMinutes(15) : setExpireOnPreviewMinutes(0);
        setCanExpireOnView(isChecked);
    };

    const dontSyncPasswordChanged = (isChecked: boolean) => {
        if (isChecked) {
            setDontSyncPassword(true);
        } else {
            setDontSyncPassword(false);
        }
    };

    const handleOpenPasswordPolicyRequirements = (event: React.MouseEvent<HTMLButtonElement>) => {
        setOpenPasswordPolicyRequirements(true);
        setAnchorEl(event.currentTarget);
    };

    const handleOpenPasswordSharingHelper = (event: React.MouseEvent<HTMLButtonElement>) => {
        setOpenPasswordSharingHelper(true);
        setAnchorEl(event.currentTarget);
    };

    const handleModalCloseCallback = () => {
        setAnchorEl(null);
        setOpenPasswordPolicyRequirements(false);
        setOpenPasswordSharingHelper(false);
    };

    const getIcon = (item: any) => {
        return <div className={`vault__passwords-ico dropdown__item-content-icon ${getPasswordTypeUIDetails(item.value).passwordTypeImage}`} />;
    };

    return (
        <>
            <Popover
                open={openPasswordPolicyRequirements}
                anchorEl={anchorEl}
                onClose={handleModalCloseCallback}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right'
                }}
                getContentAnchorEl={null}
            >
                <PasswordPolicyRequirements passwordPolicies={passwordPolicies} />
            </Popover>

            <Popover
                open={openPasswordSharingHelper}
                anchorEl={anchorEl}
                onClose={handleModalCloseCallback}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left'
                }}
                getContentAnchorEl={null}
            >
                <Box padding={2}>
                    <Typography variant='body1'>{translation('VAULT_PASSWORD_ENABLE_SHARING_HELP')}</Typography>
                </Box>
            </Popover>

            <>
                {!hasPermission ? (
                    <div className='table--empty'>{translation('NO_ORG_VAULT_PASSWORD_PERMISSIONS')}</div>
                ) : (
                    <>
                        {loaded ? (
                            <>
                                <Grid direction='column' container spacing={2} wrap='nowrap' className='edit-vault-password'>
                                    <Grid item xs={12}>
                                        <Typography variant='h1'>
                                            {viewType === FormMode.Add
                                                ? translation('NEW_PASSWORD')
                                                : translation('EDIT_PASSWORD_SERVER_VAULT_PASSWORD')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container direction='row' justifyContent='flex-start' alignItems='flex-start' spacing={3}>
                                            <Grid item xs={5}>
                                                <PasslyOutlinedBox title={translation('VAULT_PASSWORD_GENERAL_SETTINGS')}>
                                                    <Grid direction='column' container wrap='nowrap'>
                                                        {/*----------------------------------------------*/}
                                                        {/*----------------------------------------------*/}
                                                        {/*----------- General Settings Section ---------*/}
                                                        {/*----------------------------------------------*/}
                                                        {/*----------------------------------------------*/}

                                                        {viewType === FormMode.Edit ? (
                                                            <Grid item xs={12}>
                                                                <Typography noWrap variant='caption'>
                                                                    {`${translation('PASSWORD_RECORD_ID')}: ${passwordId}`}
                                                                </Typography>
                                                            </Grid>
                                                        ) : null}

                                                        <Grid item xs={12}>
                                                            <PasslyTextField
                                                                fullWidth
                                                                autoFocus
                                                                autoComplete='off'
                                                                label={translation('DISPLAY_NAME')}
                                                                id='displayName-input'
                                                                name='DisplayName'
                                                                focusfunc={validateDataChange}
                                                                onChange={handleDataChange}
                                                                type={'text'}
                                                                value={addEditVaultPassword.DisplayName}
                                                                inputProps={{ maxLength: 100 }}
                                                                error={displayNameError !== ''}
                                                                helperText={displayNameError}
                                                                required
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <PasslySelect
                                                                label={translation('VAULT_FOLDER')}
                                                                id='passwordFolder-input'
                                                                name='FolderId'
                                                                onChange={handleDataChange}
                                                                value={addEditVaultPassword.FolderId}
                                                                options={folders}
                                                                optionLabel='DisplayName'
                                                                optionValue='Id'
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <PasslyTextField
                                                                fullWidth
                                                                label={translation('DESCRIPTION')}
                                                                id='passwordDescription-input'
                                                                name='Description'
                                                                onChange={handleDataChange}
                                                                type={'text'}
                                                                value={addEditVaultPassword.Description}
                                                                inputProps={{ maxLength: 100 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <PasslyTextField
                                                                fullWidth
                                                                multiline
                                                                rows='5'
                                                                rowsMax='8'
                                                                label={translation('VAULT_PASSWORD_NOTES')}
                                                                id='passwordNotes-input'
                                                                name='Notes'
                                                                onChange={handleDataChange}
                                                                type={'text'}
                                                                value={addEditVaultPassword.Notes}
                                                            />
                                                        </Grid>

                                                        {isIndividual === 'false' ? (
                                                            <Grid item xs={12}>
                                                                <Grid container direction='row' alignItems='center'>
                                                                    <Grid item>
                                                                        <PasslyCheckbox
                                                                            name='EnableTrustedThirdPartyAccess'
                                                                            checked={addEditVaultPassword.EnableTrustedThirdPartyAccess}
                                                                            onChange={handleCheckboxDataChange}
                                                                            label={translation('VAULT_PASSWORD_ENABLE_SHARING')}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <IconButton
                                                                            size='small'
                                                                            style={{ padding: '0px' }}
                                                                            onClick={handleOpenPasswordSharingHelper}
                                                                        >
                                                                            <HelpOutline
                                                                                color='disabled'
                                                                                fontSize='small'
                                                                                style={{ padding: '0px' }}
                                                                            />
                                                                        </IconButton>
                                                                    </Grid>
                                                                    {addEditVaultPassword.EnableTrustedThirdPartyAccess &&
                                                                        viewType === FormMode.Edit && (
                                                                            <Grid item>
                                                                                <CopyComponent tooltip={shareableLink} valueToCopy={shareableLink}>
                                                                                    <span>
                                                                                        <PasslyButton variant='text' color='primary'>
                                                                                            <Link color='inherit' />
                                                                                            {translation('VAULT_PASSWORD_COPY_LINK')}
                                                                                        </PasslyButton>
                                                                                    </span>
                                                                                </CopyComponent>
                                                                            </Grid>
                                                                        )}
                                                                </Grid>
                                                            </Grid>
                                                        ) : null}
                                                    </Grid>
                                                </PasslyOutlinedBox>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <PasslyOutlinedBox title={translation('VAULT_PASSWORD_PROPERTIES')}>
                                                    <Grid direction='column' container wrap='nowrap'>
                                                        {/*-------------------------------------------------*/}
                                                        {/*-------------------------------------------------*/}
                                                        {/*----------- Password Properties Section ---------*/}
                                                        {/*-------------------------------------------------*/}
                                                        {/*-------------------------------------------------*/}
                                                        <Grid item xs={12}>
                                                            <PasslySelect
                                                                label={translation('VAULT_PASSWORD_TYPE')}
                                                                id='passwordType-input'
                                                                name={PasswordType}
                                                                onChange={handleDataChange}
                                                                value={addEditVaultPassword.PasswordType}
                                                                options={passwordTypes}
                                                                icon={getIcon}
                                                            />
                                                        </Grid>
                                                        {displayFields.ShowUsername && (
                                                            <Grid item xs={12}>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    label={translation('USERNAME')}
                                                                    id='passwordUsername-input'
                                                                    name={Username}
                                                                    onChange={handleDataChange}
                                                                    focusfunc={validateDataChange}
                                                                    type={'text'}
                                                                    value={addEditVaultPassword.Username}
                                                                    inputProps={{ maxLength: 256 }}
                                                                    disabled={isAgentDisabled}
                                                                    error={usernameError !== ''}
                                                                    required
                                                                    helperText={usernameError}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowSnmpUser && (
                                                            <Grid item xs={12}>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    autoComplete='off'
                                                                    label={translation('VAULT_PASSWORD_SNMP_USER')}
                                                                    id='snmpUser-input'
                                                                    name={SnmpUser}
                                                                    onChange={handleDataChange}
                                                                    focusfunc={validateDataChange}
                                                                    type={'text'}
                                                                    value={addEditVaultPassword.SnmpUser}
                                                                    inputProps={{ maxLength: 100 }}
                                                                    disabled={isAgentDisabled}
                                                                    error={snmpUserError !== ''}
                                                                    helperText={snmpUserError}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowPassword && (
                                                            <Grid item xs={6}>
                                                                <VaultPasswordComponent
                                                                    errorText={passwordError}
                                                                    disabled={isAgentDisabled}
                                                                    defaultStyle={true}
                                                                    vaultId={vaultId}
                                                                    formMode={viewType}
                                                                    password={addEditVaultPassword.Password}
                                                                    passwordId={passwordId}
                                                                    label={translation('PASSWORD')}
                                                                    passwordComplexity={addEditVaultPassword.Complexity}
                                                                    showPasswordStrength={true}
                                                                    showGenerateButton={true}
                                                                    returnGeneratedPassword={generatePasswordGeneric}
                                                                    showPlaceholder={false}
                                                                    showExpiration={true}
                                                                    showHistoryIcon={viewType === FormMode.Edit}
                                                                    historyPasswordType={0}
                                                                    required
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowSnmpCommunityString && (
                                                            <Grid item xs={6}>
                                                                <VaultPasswordComponent
                                                                    errorText={communityStringError}
                                                                    disabled={isAgentDisabled}
                                                                    defaultStyle={true}
                                                                    vaultId={vaultId}
                                                                    formMode={viewType}
                                                                    password={addEditVaultPassword.SnmpCommunityString}
                                                                    passwordId={passwordId}
                                                                    label={translation('VAULT_PASSWORD_SNMP_COMMUNITY_STRING')}
                                                                    passwordComplexity={addEditVaultPassword.Complexity}
                                                                    showPasswordStrength={true}
                                                                    showGenerateButton={true}
                                                                    returnGeneratedPassword={generateCommunityString}
                                                                    showPlaceholder={false}
                                                                    showExpiration={true}
                                                                    showHistoryIcon={viewType === FormMode.Edit}
                                                                    historyPasswordType={1}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowSnmpAuthenticationPassword && (
                                                            <Grid item xs={6}>
                                                                <VaultPasswordComponent
                                                                    errorText={authenticationPasswordError}
                                                                    disabled={isAgentDisabled}
                                                                    defaultStyle={true}
                                                                    vaultId={vaultId}
                                                                    formMode={viewType}
                                                                    password={addEditVaultPassword.SnmpAuthenticationPassword}
                                                                    passwordId={passwordId}
                                                                    label={translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PASSWORD')}
                                                                    passwordComplexity={addEditVaultPassword.Complexity}
                                                                    showPasswordStrength={true}
                                                                    showGenerateButton={true}
                                                                    returnGeneratedPassword={generatePasswordAuth}
                                                                    showPlaceholder={false}
                                                                    showExpiration={true}
                                                                    showHistoryIcon={viewType === FormMode.Edit}
                                                                    historyPasswordType={2}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowSnmpPrivacyPassword && (
                                                            <Grid item xs={6}>
                                                                <VaultPasswordComponent
                                                                    errorText={privacyPasswordError}
                                                                    disabled={isAgentDisabled}
                                                                    defaultStyle={true}
                                                                    vaultId={vaultId}
                                                                    formMode={viewType}
                                                                    password={addEditVaultPassword.SnmpPrivacyPassword}
                                                                    passwordId={passwordId}
                                                                    label={translation('VAULT_PASSWORD_SNMP_PRIVACY_PASSWORD')}
                                                                    passwordComplexity={addEditVaultPassword.Complexity}
                                                                    showPasswordStrength={true}
                                                                    showGenerateButton={true}
                                                                    returnGeneratedPassword={generatePasswordPrivacy}
                                                                    showPlaceholder={false}
                                                                    showExpiration2={true}
                                                                    showHistoryIcon={viewType === FormMode.Edit}
                                                                    historyPasswordType={3}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowDomain && (
                                                            <Grid item xs={12}>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    label={translation('VAULT_PASSWORD_DOMAIN')}
                                                                    id='passwordDomain-input'
                                                                    name={Domain}
                                                                    focusfunc={validateDataChange}
                                                                    onChange={handleDataChange}
                                                                    type={'text'}
                                                                    value={addEditVaultPassword.Domain}
                                                                    inputProps={{ maxLength: 100 }}
                                                                    disabled={isAgentDisabled}
                                                                    error={domainError !== ''}
                                                                    helperText={domainError}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {(displayFields.ShowMachineName || displayFields.ShowSnmpAgentPort) && (
                                                            <Grid item xs={12}>
                                                                <Grid container direction='row'>
                                                                    {displayFields.ShowMachineName && (
                                                                        <Grid item xs={displayFields.ShowSnmpAgentPort ? 9 : 12}>
                                                                            <Box mr={displayFields.ShowSnmpAgentPort ? 1 : 0}>
                                                                                <PasslyTextField
                                                                                    fullWidth
                                                                                    label={translation('VAULT_PASSWORD_MACHINE_NAME')}
                                                                                    id='passwordMachineName-input'
                                                                                    name={MachineName}
                                                                                    focusfunc={validateDataChange}
                                                                                    onChange={handleDataChange}
                                                                                    type={'text'}
                                                                                    value={addEditVaultPassword.MachineName}
                                                                                    inputProps={{ maxLength: 100 }}
                                                                                    error={machineNameError !== ''}
                                                                                    helperText={machineNameError}
                                                                                />
                                                                            </Box>
                                                                        </Grid>
                                                                    )}
                                                                    {displayFields.ShowSnmpAgentPort && (
                                                                        <Grid item xs={3}>
                                                                            <InputNumber
                                                                                id='snmpAgentPort-input'
                                                                                label={`${translation('VAULT_PASSWORD_SNMP_AGENT_PORT')}`}
                                                                                name={SnmpAgentPort}
                                                                                value={addEditVaultPassword.SnmpAgentPort}
                                                                                onValueChange={handleDataChange}
                                                                                showButtons
                                                                                idecrementButtonClassName={''}
                                                                                min={0}
                                                                                max={65535}
                                                                                errorMessage={snmpAgentPortError ? '0-65535' : undefined}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowWebsiteUrl && (
                                                            <Grid item xs={12}>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    label={translation('VAULT_PASSWORD_WEB')}
                                                                    id='websiteUrl-input'
                                                                    name={WebsiteUrl}
                                                                    onChange={handleDataChange}
                                                                    type={'url'}
                                                                    value={addEditVaultPassword.WebsiteUrl}
                                                                    inputProps={{ maxLength: 100 }}
                                                                    error={webUrlError !== ''}
                                                                    helperText={webUrlError}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowSnmpAuthenticationProfile && displayFields.ShowSnmpEncryptionProtocol && (
                                                            <Grid item xs={12}>
                                                                <Grid container direction='row'>
                                                                    <Grid item xs={6}>
                                                                        <Box mr={1}>
                                                                            <PasslySelect
                                                                                fullWidth
                                                                                label={translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PROFILE')}
                                                                                id='passwordSnmpAuthenticationProfile-input'
                                                                                name='SnmpAuthenticationProfile'
                                                                                onChange={handleDataChange}
                                                                                type={'text'}
                                                                                value={addEditVaultPassword.SnmpAuthenticationProfile}
                                                                                options={authenticationProfiles}
                                                                            />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6}>
                                                                        <PasslySelect
                                                                            fullWidth
                                                                            label={translation('VAULT_PASSWORD_SNMP_ENCRYPTION_PROTOCOL')}
                                                                            id='passwordSnmpEncryptionProtocol-input'
                                                                            name='SnmpEncryptionProtocol'
                                                                            onChange={handleDataChange}
                                                                            type={'text'}
                                                                            value={addEditVaultPassword.SnmpEncryptionProtocol}
                                                                            options={encryptionProtocols}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowDBTypes && (
                                                            <Grid item xs={12}>
                                                                <PasslySelect
                                                                    fullWidth
                                                                    label={translation('VAULT_PASSWORD_DB_TYPE')}
                                                                    id='dbType-input'
                                                                    name={DBType}
                                                                    onChange={handleDataChange}
                                                                    type={'text'}
                                                                    value={addEditVaultPassword.DBType}
                                                                    options={dbTypes}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {displayFields.ShowDBOther && (
                                                            <Grid item xs={12}>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    label={translation('VAULT_PASSWORD_DB_TYPE')}
                                                                    id='dbTypeOther-input'
                                                                    name='DBTypeOther'
                                                                    onChange={handleDataChange}
                                                                    type={'text'}
                                                                    value={addEditVaultPassword.DBTypeOther}
                                                                    inputProps={{ maxLength: 100 }}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {/*----------------------------------------------------*/}
                                                        {/*----------------------------------------------------*/}
                                                        {/*------ End conditional password type fields --------*/}
                                                        {/*----------------------------------------------------*/}
                                                        {/*----------------------------------------------------*/}
                                                        <Grid item xs={12}>
                                                            <PasslyCheckbox
                                                                name='active'
                                                                checked={addEditVaultPassword.IgnoreVaultPasswordPolicy}
                                                                onChange={handleUseVaultPasswordPolicy}
                                                                label={translation('VAULT_PASSWORD_IGNORE_POLICY')}
                                                            />
                                                            <Grid item xs={12}>
                                                                <Grid container direction='row'>
                                                                    <Grid item xs={6} className='vault__passwords__policy mr-8'>
                                                                        <Box mr={1}>
                                                                            <PasslySelect
                                                                                label={translation('VAULT_PASSWORD_POLICY')}
                                                                                id='passwordComplexity-input'
                                                                                name='Complexity'
                                                                                onChange={handleDataChange}
                                                                                value={addEditVaultPassword.Complexity}
                                                                                options={passwordPolicies}
                                                                                optionLabel='label'
                                                                                optionValue='value'
                                                                                disabled={addEditVaultPassword.IgnoreVaultPasswordPolicy}
                                                                            />
                                                                            <IconButton
                                                                                className='vault__passwords__policy-icon'
                                                                                size='small'
                                                                                style={{ padding: '0px' }}
                                                                                onClick={handleOpenPasswordPolicyRequirements}
                                                                            >
                                                                                <HelpOutline
                                                                                    color='disabled'
                                                                                    fontSize='small'
                                                                                    style={{ padding: '0px' }}
                                                                                />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6} className='vault__passwords__policy'>
                                                                        <InputNumber
                                                                            id='daysToExpire-input'
                                                                            name='DaysToExpire'
                                                                            label={translation('VAULT_PASSWORD_DAYS_TO_EXPIRATION')}
                                                                            value={addEditVaultPassword.DaysToExpire}
                                                                            onValueChange={handleDataChange}
                                                                            showButtons
                                                                            idecrementButtonClassName={''}
                                                                            min={0}
                                                                            max={9999}
                                                                            errorMessage={daysToExpireError ? '0-9999' : undefined}
                                                                            disabled={!addEditVaultPassword.IgnoreVaultPasswordPolicy}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Grid container direction='row' className='vault__passwords__expire--wrapper'>
                                                                <Grid item xs={6}>
                                                                    <PasslyCheckbox
                                                                        name=''
                                                                        checked={canExpireOnView}
                                                                        onChange={handleCanExpireOnView}
                                                                        label={translation('VAULT_PASSWORD_EXPIRE_ON_VIEW')}
                                                                    />
                                                                </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Box mr={1}>
                                                                            <InputNumber
                                                                                id='expireOnPreviewHours-input'
                                                                                name='ExpireOnPreviewHours'
                                                                                value={expireOnPreviewHours}
                                                                                onValueChange={(e) =>
                                                                                    setExpireOnPreviewHours(
                                                                                        parseInt(e.target.value as string)
                                                                                    )}
                                                                                showButtons
                                                                                idecrementButtonClassName={''}
                                                                                min={0}
                                                                                max={maxExpireAfterPreviewHours}
                                                                                errorMessage={expireOnPreviewHoursError ? '0-' + maxExpireAfterPreviewHours : undefined}
                                                                                suffix={' ' + translation('HOURS_SHORT').toLocaleLowerCase()}
                                                                                disabled={!canExpireOnView}
                                                                            />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <InputNumber
                                                                            id='expireOnPreviewMinutes-input'
                                                                            name='ExpireOnPreviewMinutes'
                                                                            value={expireOnPreviewMinutes}
                                                                            onValueChange={(e) =>
                                                                                setExpireOnPreviewMinutes(
                                                                                    parseInt(e.target.value as string)
                                                                                )}
                                                                            showButtons
                                                                            idecrementButtonClassName={''}
                                                                            min={0}
                                                                            max={59}
                                                                            errorMessage={expireOnPreviewMinutesError ? '0-59' : undefined}
                                                                            suffix={' ' + translation('MINUTES_SHORT')}
                                                                            disabled={!canExpireOnView}
                                                                        />
                                                                    </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        {displayFields.ShowSyncAgent && (
                                                            <>
                                                                <Grid item xs={12}>
                                                                    <PasslyCheckbox
                                                                        name='dontSyncPassword'
                                                                        checked={dontSyncPassword}
                                                                        onChange={dontSyncPasswordChanged}
                                                                        label={translation('DO_NOT_SYNC')}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <PasslySelect
                                                                        label={translation('VAULT_PASSWORD_SYNC_AGENT')}
                                                                        id='passwordSyncAgent-input'
                                                                        name={AgentProfileId}
                                                                        onChange={handleDataChange}
                                                                        value={addEditVaultPassword.AgentProfileId}
                                                                        placeholder={`*${translation('VAULT_PASSWORD_CHANGE_PWD_WARNING')}`}
                                                                        options={availableSyncAgents}
                                                                        optionLabel='label'
                                                                        optionValue='value'
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </PasslyOutlinedBox>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container alignContent='flex-end' justifyContent='flex-end'>
                                    <Grid item>{saveError !== '' && <div className='modal__error'>{saveError}</div>}</Grid>
                                </Grid>
                                <PageActions
                                    primaryBtn={viewType === FormMode.Add ? translation('ADD_PASSWORD') : translation('SAVE')}
                                    secondaryBtn={translation('CANCEL')}
                                    onClickPrimary={handleSubmit}
                                    onClickSecondary={handleCancel}
                                />
                            </>
                        ) : (
                            <PageLoader />
                        )}
                    </>
                )}
            </>
        </>
    );
};

export default AddEditVaultPasswordPage;
